// 파티 멤버들의 기본 클래스 (Player, Companion들의 공통 기능)
class Party extends Entity {
  constructor(scene, x = 0, y = 0, z = 0, config = {}) {
    super(scene, x, y, z, config);

    // 체력바 시스템 (Party 전용 - Enemy는 체력바 없음)
    this.healthBar = null;

    // 부채꼴 공격 시스템 (Party 전용 - Player와 Companion 공통)
    this.enemies = []; // 적들 참조
    this.attackAngle = config.ATTACK_ANGLE || Math.PI / 3; // 공격 중심각 (기본값: 60도)

    // 부채꼴 공격 범위 시각화 (Party 전용)
    this.attackVisualMesh = null; // 공격 범위 시각화 메쉬
    this.showAttackVisual = config.SHOW_ATTACK_VISUAL !== false; // 부채꼴 표시 여부 (기본값: true)
    this.attackSectors = config.ATTACK_SECTORS || 4; // 부채꼴 세그먼트 수

    // 기본 스탯 저장 (파티 버프 적용을 위한 기준값)
    this.baseStats = {
      attackDamage: this.attackDamage,
      attackRange: this.attackRange,
      attackCooltime: this.attackCooltime,
      maxHealth: this.maxHealth,
      moveSpeed: this.moveSpeed,
    };
  }

  // 한국어 주석: 파티 버프 계수를 최종 스탯에 적용 (덧셈 방식)
  // 파티 버프 계수를 최종 스탯에 적용
  applyPartyBuffs() {
    // 공격력 적용
    this.attackDamage =
      this.baseStats.attackDamage *
      (1 + Party.partyBuffCoefficients.ATTACK_DAMAGE);

    // 공격 범위 적용
    this.attackRange =
      this.baseStats.attackRange *
      (1 + Party.partyBuffCoefficients.ATTACK_RANGE);

    // 공격 쿨타임 적용
    this.attackCooltime =
      this.baseStats.attackCooltime *
      (1 + Party.partyBuffCoefficients.ATTACK_COOLTIME);

    // 최대 체력 적용
    const oldMaxHealth = this.maxHealth;
    this.maxHealth =
      this.baseStats.maxHealth * (1 + Party.partyBuffCoefficients.MAX_HEALTH);

    // 현재 체력도 비율 유지하며 조정
    if (oldMaxHealth > 0) {
      const healthRatio = this.health / oldMaxHealth;
      this.health = this.maxHealth * healthRatio;
    }

    // 이동 속도 적용
    this.moveSpeed =
      this.baseStats.moveSpeed * (1 + Party.partyBuffCoefficients.MOVE_SPEED);

    // 체력바 업데이트
    if (this.healthBar && this.isAlive) {
      this.healthBar.updateHealth(this.health, this.maxHealth);
    }

    // 공격 범위 시각화 재생성 (범위가 변경되었을 때)
    if (this.attackVisualMesh) {
      this.scene.remove(this.attackVisualMesh);
      this.attackVisualMesh = null;
      this.createAttackVisual();
    }
  }

  // 기본 스탯 업데이트 (업그레이드 등으로 기본 스탯이 변경될 때 호출)
  updateBaseStats() {
    this.baseStats = {
      attackDamage: this.attackDamage,
      attackRange: this.attackRange,
      attackCooltime: this.attackCooltime,
      maxHealth: this.maxHealth,
      moveSpeed: this.moveSpeed,
    };
  }

  // 체력바 생성
  createHealthBar() {
    if (this.healthBar) {
      this.healthBar.destroy();
    }

    this.healthBar = new HealthBar(this.scene, this);
    this.healthBar.updateHealth(this.health, this.maxHealth);
  }

  // 체력바 업데이트
  updateHealthBar(camera) {
    if (this.healthBar && this.isAlive) {
      this.healthBar.update(camera);
    }
  }

  // 체력바 표시/숨김
  showHealthBar(visible = true) {
    if (this.healthBar) {
      this.healthBar.setVisible(visible);
    }
  }

  // 체력 회복
  heal(amount) {
    if (!this.isAlive) return;

    this.health = Math.min(this.maxHealth, this.health + amount);

    // 체력바 업데이트
    if (this.healthBar && this.isAlive) {
      this.healthBar.updateHealth(this.health, this.maxHealth);
    }
  }

  // 데미지 받기 오버라이드 (체력바 업데이트 포함)
  takeDamage(damage, attackerPosition = null) {
    const wasAlive = this.isAlive;
    const result = super.takeDamage(damage, attackerPosition);

    // 체력바 업데이트 (사망하기 전에만)
    if (this.healthBar && this.health > 0 && wasAlive) {
      this.healthBar.updateHealth(this.health, this.maxHealth);
    }

    return result;
  }

  // 죽음 처리 오버라이드 (체력바 및 부채꼴 시각화 제거 포함)
  die() {
    super.die();

    // 체력바 제거
    if (this.healthBar) {
      this.healthBar.destroy();
      this.healthBar = null;
    }

    // 부채꼴 시각화 제거
    this.removeAttackVisual();
  }

  // 정리 오버라이드 (체력바 및 부채꼴 시각화 정리 포함)
  dispose() {
    if (this.healthBar) {
      this.healthBar.destroy();
      this.healthBar = null;
    }

    // 부채꼴 시각화 제거
    this.removeAttackVisual();

    super.dispose();
  }

  // === 부채꼴 공격 시스템 ===

  // 적들 참조 설정 (Game이나 Manager에서 호출)
  setEnemies(enemies) {
    this.enemies = enemies;
  }

  // 부채꼴 공격 범위 내의 모든 적 찾기
  findEnemiesInSector() {
    const myPos = this.getPosition();

    return CollisionUtils.findEnemiesInSector(
      myPos,
      this.model.rotation.y,
      this.enemies,
      this.attackRange,
      this.attackAngle
    );
  }

  // 부채꼴 범위의 모든 적 공격
  attackEnemiesInSector(currentTime) {
    // 쿨다운 체크
    if (currentTime - this.lastAttackTime < this.attackCooltime) {
      return false;
    }

    // 부채꼴 범위 내의 모든 적 찾기
    const enemiesInRange = this.findEnemiesInSector();

    if (enemiesInRange.length === 0) {
      return false;
    }

    // 공격 애니메이션 재생
    this.isAttacking = true;
    this.playAnimation("attack");

    // 공격 범위 시각화 (공격 시 강조)
    this.setAttackVisualHighlight(true);

    // 한국어 주석: 공격 애니메이션 duration의 50% 지점에 데미지 적용 (즉시 데미지 제거)
    const attackDelay = this.attackAction
      ? this.attackAction.getClip().duration * 0.5
      : this.attackCooltime * 0.5;

    // 데미지 적용을 지연시킴 (즉시 데미지 제거)
    const myPos = this.getPosition();
    const attackDamage = this.attackDamage;

    setTimeout(() => {
      // 공격 시점에 범위 내 적들을 다시 확인 (이동했을 수 있음)
      const currentEnemiesInRange = this.findEnemiesInSector();
      currentEnemiesInRange.forEach((enemy) => {
        if (enemy.isAlive && enemy.canTakeDamage()) {
          enemy.takeDamage(attackDamage, myPos);
        }
      });
    }, attackDelay * 1000);

    // 공격 시간 업데이트
    this.lastAttackTime = currentTime;

    return true;
  }

  // === 부채꼴 시각화 시스템 ===

  // 부채꼴 공격 범위 시각화 생성
  createAttackVisual() {
    if (!this.showAttackVisual || this.attackVisualMesh) return;

    // 부채꼴 형태의 지오메트리 생성
    const geometry = new THREE.ConeGeometry(
      this.attackRange, // 반지름
      0.1, // 높이 (평평하게)
      this.attackSectors, // 세그먼트 수
      1, // 높이 세그먼트
      false, // 열린 끝
      -this.attackAngle / 2, // 시작 각도
      this.attackAngle // 중심각
    );

    // 반투명 녹색 재질
    const material = new THREE.MeshBasicMaterial({
      color: 0x44ff44,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
    });

    // 메쉬 생성
    this.attackVisualMesh = new THREE.Mesh(geometry, material);

    // 위치와 회전 업데이트
    this.updateAttackVisualPosition();

    // 씬에 추가
    this.scene.add(this.attackVisualMesh);
  }

  // 부채꼴 위치 업데이트 (위치와 회전에 맞춰)
  updateAttackVisualPosition() {
    if (!this.attackVisualMesh || !this.model || !this.isAlive) return;

    const myPos = this.getPosition();
    this.attackVisualMesh.position.set(myPos.x, 0.05, myPos.z);
    this.attackVisualMesh.rotation.y = this.model.rotation.y; // 방향에 맞춰
  }

  // 공격 시 일시적으로 색상 강조
  setAttackVisualHighlight(isAttack) {
    if (!this.attackVisualMesh || !this.isAlive) return;

    EffectUtils.updateAttackVisualColor(this.attackVisualMesh, isAttack);
  }

  // 공격 시각화 업데이트 (매 프레임 호출)
  updateAttackVisualization() {
    if (!this.showAttackVisual || !this.attackVisualMesh) return;

    // 위치와 회전에 따라 부채꼴 위치 업데이트
    this.updateAttackVisualPosition();

    // 부채꼴 범위 내 적 확인하여 색상 변경
    this.updateAttackVisualColor();
  }

  // 부채꼴 색상 업데이트 (적 감지에 따른 색상 변경)
  updateAttackVisualColor() {
    if (!this.attackVisualMesh || !this.isAlive) return;

    // 부채꼴 범위 내의 적 확인
    const enemiesInRange = this.findEnemiesInSector();
    const isEnemyInRange = enemiesInRange.length > 0;

    EffectUtils.updateAttackVisualColor(this.attackVisualMesh, isEnemyInRange);
  }

  // 부채꼴 시각화 제거
  removeAttackVisual() {
    if (this.attackVisualMesh) {
      this.scene.remove(this.attackVisualMesh);
      this.attackVisualMesh = null;
    }
  }
}

// 한국어 주석: 파티 버프 계수 (모든 파티 멤버에게 공통으로 적용되는 배수, 덧셈 방식이므로 초기값 0)
// 파티 버프 계수 (모든 파티 멤버에게 공통으로 적용되는 배수)
Party.partyBuffCoefficients = {
  ATTACK_DAMAGE: 0,
  ATTACK_RANGE: 0,
  ATTACK_COOLTIME: 0,
  MAX_HEALTH: 0,
  MOVE_SPEED: 0,
};

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = Party;
} else {
  window.Party = Party;
}

// webpack 빌드를 위해 default export 추가
export default Party;
